import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { create } from '@bufbuild/protobuf';
import { ReceiptItemSchema, SaleItemSchema } from '../../generated/receipt/v1/receipt_pb';
import { createReceipt } from '@/api/receipt';
import { QuantityInput } from '@/components/QuantityInput';
import { PriceInput } from '@/components/PriceInput';

interface ManualItem {
  description: string;
  price: number;
  quantity: number;
}

interface ManualSaleItem {
  item: string;
  discount: number;
}

const PAYMENT_METHODS = ['Visa', 'Mastercard', 'Amex', 'Discover', 'Cash', 'Debit', 'Apple Pay', 'Google Pay'];

export default function ManualReceiptPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [store, setStore] = useState('');
  const [date, setDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [payment, setPayment] = useState('');
  const [tax, setTax] = useState<number>(0);
  const [items, setItems] = useState<ManualItem[]>([{ description: '', price: 0, quantity: 1 }]);
  const [saleItems, setSaleItems] = useState<ManualSaleItem[]>([]);

  // Calculate totals
  const itemsTotal = items.reduce((sum, item) => sum + item.price, 0);
  const discountsTotal = saleItems.reduce((sum, sale) => sum + Math.abs(sale.discount), 0);
  const total = itemsTotal - discountsTotal + tax;

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const MAX_INPUT_CHARS = 500;
  const getCharLimitHelper = (value: string) =>
    value.length >= MAX_INPUT_CHARS ? `${MAX_INPUT_CHARS}/${MAX_INPUT_CHARS} characters` : undefined;

  const handleAddItem = () => {
    setItems([...items, { description: '', price: 0, quantity: 1 }]);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index: number, field: keyof ManualItem, value: string | number) => {
    const updatedItems = [...items];
    if (field === 'description') {
      updatedItems[index].description = value as string;
    } else if (field === 'quantity') {
      updatedItems[index].quantity = Number(value) || 1;
    } else if (field === 'price') {
      updatedItems[index].price = Number(value) || 0;
    }
    setItems(updatedItems);
  };

  const handleAddSaleItem = () => {
    setSaleItems([...saleItems, { item: '', discount: 0 }]);
  };

  const handleRemoveSaleItem = (index: number) => {
    setSaleItems(saleItems.filter((_, i) => i !== index));
  };

  const handleSaleItemChange = (index: number, field: keyof ManualSaleItem, value: string | number) => {
    const updated = [...saleItems];
    if (field === 'item') {
      updated[index].item = value as string;
    } else {
      updated[index].discount = Number(value) || 0;
    }
    setSaleItems(updated);
  };

  const handleSubmit = async () => {
    // Validation
    if (!store.trim()) {
      setError('Store name is required');
      return;
    }
    if (!date) {
      setError('Date is required');
      return;
    }
    const validItems = items.filter(item => item.description.trim());
    if (validItems.length === 0) {
      setError('At least one item is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      // Convert to protobuf format
      const protoItems = validItems.map(item =>
        create(ReceiptItemSchema, {
          description: item.description,
          price: item.price,
          quantity: item.quantity,
        })
      );

      const protoSaleItems = saleItems
        .filter(sale => sale.item.trim())
        .map(sale =>
          create(SaleItemSchema, {
            item: sale.item,
            discount: sale.discount,
          })
        );

      const response = await createReceipt({
        store: store.trim(),
        date: date.format('YYYY-MM-DD'),
        payment: payment || '',
        tax,
        total,
        items: protoItems,
        saleItems: protoSaleItems,
      });

      // Navigate to the receipt detail page
      navigate(`/receipt/${response.receiptId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create receipt');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Add Receipt Manually
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enter receipt details without uploading an image.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Store Information */}
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Store Information
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Store Name"
                value={store}
                onChange={(e) => setStore(e.target.value)}
                helperText={getCharLimitHelper(store)}
                fullWidth
                required
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <DatePicker
                  label="Date"
                  value={date}
                  onChange={(newValue) => setDate(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, required: true },
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    id="payment-method"
                    value={payment}
                    label="Payment Method"
                    onChange={(e) => setPayment(e.target.value)}
                    MenuProps={{ disableRestoreFocus: true }}
                  >
                    <MenuItem value="">None</MenuItem>
                    {PAYMENT_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Items */}
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Items
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(itemsTotal)}
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Qty</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>Price</TableCell>
                    <TableCell align="right" sx={{ width: 60 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Item description"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          helperText={getCharLimitHelper(item.description)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <QuantityInput
                          value={item.quantity}
                          onChange={(value) => handleItemChange(index, 'quantity', String(value))}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <PriceInput
                          value={item.price || ''}
                          onChange={(value) => handleItemChange(index, 'price', value)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(index)}
                          disabled={items.length === 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={handleAddItem}
              >
                Add Item
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Discounts */}
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Discounts
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                -{formatCurrency(discountsTotal)}
              </Typography>
            </Box>
            {saleItems.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>Discount</TableCell>
                      <TableCell align="right" sx={{ width: 60 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {saleItems.map((sale, index) => (
                      <TableRow key={index}>
                        <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Discount item"
                          value={sale.item}
                          onChange={(e) => handleSaleItemChange(index, 'item', e.target.value)}
                          helperText={getCharLimitHelper(sale.item)}
                        />
                        </TableCell>
                        <TableCell align="right">
                          <PriceInput
                            value={sale.discount || ''}
                            onChange={(value) => handleSaleItemChange(index, 'discount', value)}
                            allowNegative
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveSaleItem(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              onClick={handleAddSaleItem}
            >
              Add Discount
            </Button>
          </CardContent>
        </Card>

        {/* Summary */}
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Summary
            </Typography>
            <Stack spacing={1}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Items Subtotal:</Typography>
                <Typography variant="body2">{formatCurrency(itemsTotal)}</Typography>
              </Box>
              {discountsTotal > 0 && (
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Discounts:</Typography>
                  <Typography variant="body2" color="success.main">
                    -{formatCurrency(discountsTotal)}
                  </Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2">Tax:</Typography>
                <PriceInput
                  value={tax || ''}
                  onChange={(value) => setTax(Number(value) || 0)}
                  required={false}
                  sx={{ width: 100 }}
                />
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" fontWeight="bold">
                  Total:
                </Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {formatCurrency(total)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ py: 1.5, borderRadius: 2, boxShadow: 'none' }}
        >
          {isSubmitting ? 'Saving...' : 'Save Receipt'}
        </Button>
      </Stack>
    </Box>
  );
}
